'use client'

import { createErrorToast, createToast } from '@sushiswap/notifications'
import { DialogType, useDialog } from '@sushiswap/ui'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { SupportedChainId } from 'src/config'
import { APPROVE_TAG_SWAP } from 'src/lib/constants'
import type { UseEvmTradeReturn } from 'src/lib/hooks/react-query'
import { useSlippageTolerance } from 'src/lib/hooks/useSlippageTolerance'
import { logger } from 'src/lib/logger'
import {
  type TrailsIntentReceipt,
  commitIntent,
  executeIntent,
  waitIntentReceipt,
} from 'src/lib/swap/trails'
import { isUserRejectedError } from 'src/lib/wagmi/errors'
import { useApproved } from 'src/lib/wagmi/systems/Checker/provider'
import { Amount } from 'sushi'
import {
  type EvmAddress,
  type EvmChainId,
  type EvmCurrency,
  getEvmCurrencyAddress,
  isEvmChainId,
} from 'sushi/evm'
import { type SendTransactionReturnType, stringify } from 'viem'
import {
  useConnection,
  usePublicClient,
  useSendTransaction,
  useWaitForTransactionReceipt,
} from 'wagmi'
import { useRefetchBalances } from '~evm/_common/ui/balance-provider/use-refetch-balances'
import { usePrices } from '~evm/_common/ui/price-provider/price-provider/use-prices'
import { useDetailsInteractionTracker } from '../../../_ui/details-interaction-tracker-provider'
import { isUnwrapTrade, isWrapTrade } from '../common'
import {
  useDerivedStateSimpleSwap,
  useEvmSimpleSwapTrade,
} from '../derivedstate-simple-swap-provider'
import type { UseSimpleSwapTradeReviewBaseReturn } from './use-simple-swap-trade-review'

export function useEvmSimpleSwapTradeReview(): UseSimpleSwapTradeReviewBaseReturn {
  const { state: _state } = useDerivedStateSimpleSwap<
    EvmChainId & SupportedChainId
  >()

  const state = isEvmChainId(_state.chainId)
    ? _state
    : {
        chainId: undefined,
        token0: undefined,
        token1: undefined,
      }

  return _useEvmSimpleSwapTradeReview(state)
}

function _useEvmSimpleSwapTradeReview({
  chainId,
  token0,
  token1,
}: {
  chainId: (EvmChainId & SupportedChainId) | undefined
  token0: EvmCurrency | undefined
  token1: EvmCurrency | undefined
}): UseSimpleSwapTradeReviewBaseReturn {
  const {
    mutate: { setSwapAmount, setSwapInProgress },
  } = useDerivedStateSimpleSwap<EvmChainId & SupportedChainId>()

  const {
    mutate: { resetDetailsTrackedState },
  } = useDetailsInteractionTracker()

  const { address } = useConnection()

  const client = usePublicClient({ chainId })

  const { approved } = useApproved(APPROVE_TAG_SWAP)
  const { open: confirmDialogOpen } = useDialog(DialogType.Confirm)
  const { open: reviewDialogOpen } = useDialog(DialogType.Review)

  const [slippagePercent] = useSlippageTolerance()

  const tradeRef = useRef<UseEvmTradeReturn | null>(null)
  const committedIntentIdRef = useRef<string | null>(null)
  const [intentFulfilled, setIntentFulfilled] = useState<
    'idle' | 'pending' | 'success' | 'error'
  >('idle')

  const enabled = Boolean(
    chainId &&
      approved &&
      address &&
      (confirmDialogOpen || reviewDialogOpen) &&
      intentFulfilled === 'idle',
  )

  // Once the user dismisses both dialogs, clear any terminal state so the
  // next swap re-enables quote fetching (gated on intentFulfilled === 'idle').
  useEffect(() => {
    if (
      !confirmDialogOpen &&
      !reviewDialogOpen &&
      (intentFulfilled === 'success' || intentFulfilled === 'error')
    ) {
      setIntentFulfilled('idle')
    }
  }, [confirmDialogOpen, reviewDialogOpen, intentFulfilled])

  const {
    data: trade,
    isFetching: isSwapQueryFetching,
    isSuccess: isSwapQuerySuccess,
    isError: isSwapQueryError,
    error: swapQueryError,
  } = useEvmSimpleSwapTrade(enabled)

  const { refetchChain: refetchBalances } = useRefetchBalances()

  const isWrap = isWrapTrade(token0, token1)
  const isUnwrap = isUnwrapTrade(token0, token1)

  const onSwapSuccess = useCallback(
    async (hash: SendTransactionReturnType) => {
      if (!trade || !chainId) return

      try {
        const ts = new Date().getTime()
        const txReceiptPromise = client.waitForTransactionReceipt({
          hash,
        })

        // Notify Trails the deposit tx was signed
        if (committedIntentIdRef.current && address) {
          await executeIntent(committedIntentIdRef.current, address)
        }

        // Chain: wait for tx to mine, then wait for intent fulfillment
        const intentId = committedIntentIdRef.current
        const fullPromise = txReceiptPromise.then(async (receipt) => {
          if (!intentId) {
            setIntentFulfilled('success')
            return receipt
          }

          // A thrown receipt (timeout, HTTP error) or any non-SUCCEEDED
          // terminal status must transition the dialog out of 'pending'.
          let intentReceipt: TrailsIntentReceipt
          try {
            intentReceipt = await waitIntentReceipt(intentId)
          } catch (err) {
            setIntentFulfilled('error')
            throw err
          }

          if (intentReceipt.status !== 'SUCCEEDED') {
            setIntentFulfilled('error')
            throw new Error(`Swap intent ${intentReceipt.status.toLowerCase()}`)
          }

          setIntentFulfilled('success')
          return receipt
        })

        void createToast({
          account: address,
          type: 'swap',
          chainId: chainId,
          txHash: hash,
          promise: fullPromise,
          summary: {
            pending: `${
              isWrap ? 'Wrapping' : isUnwrap ? 'Unwrapping' : 'Swapping'
            } ${trade.amountIn?.toSignificant(6)} ${
              trade.amountIn?.currency.symbol
            } ${
              isWrap ? 'to' : isUnwrap ? 'to' : 'for'
            } ${trade.amountOut?.toSignificant(6)} ${
              trade.amountOut?.currency.symbol
            }`,
            completed: `${
              isWrap ? 'Wrap' : isUnwrap ? 'Unwrap' : 'Swap'
            } ${trade.amountIn?.toSignificant(6)} ${
              trade.amountIn?.currency.symbol
            } ${
              isWrap ? 'to' : isUnwrap ? 'to' : 'for'
            } ${trade.amountOut?.toSignificant(6)} ${
              trade.amountOut?.currency.symbol
            }`,
            failed: `Something went wrong when trying to ${
              isWrap ? 'wrap' : isUnwrap ? 'unwrap' : 'swap'
            } ${trade.amountIn?.currency.symbol} ${
              isWrap ? 'to' : isUnwrap ? 'to' : 'for'
            } ${trade.amountOut?.currency.symbol}`,
          },
          timestamp: ts,
          groupTimestamp: ts,
        })

        // Don't await — let intent fulfillment run in the background.
        // The toast tracks fullPromise, and intentFulfilled state drives the dialog.
        // NB: intentFulfilled stays in its terminal state ('success' or 'error') until
        // the user dismisses the dialog — see the reset effect below.
        // The rejection is already consumed by createToast's .catch; swallow it
        // here so this independent chain doesn't produce an unhandled rejection.
        void fullPromise
          .finally(() => {
            refetchBalances(chainId)
            setSwapInProgress(false)
          })
          .catch(() => {})
      } finally {
        setSwapAmount('')
        refetchBalances(chainId)
        resetDetailsTrackedState()
      }
    },
    [
      setSwapAmount,
      trade,
      chainId,
      client,
      address,
      isWrap,
      isUnwrap,
      refetchBalances,
      resetDetailsTrackedState,
      setSwapInProgress,
    ],
  )

  const onSwapError = useCallback(
    (e: Error) => {
      if (isUserRejectedError(e)) {
        return
      }

      const tokenFrom = trade?.amountIn?.currency
        ? getEvmCurrencyAddress(trade?.amountIn.currency)
        : 'unknown'
      const tokenTo = trade?.amountOut?.currency
        ? getEvmCurrencyAddress(trade?.amountOut.currency)
        : 'unknown'
      const tx = stringify(trade?.tx)

      logger.error(e, {
        location: 'SimpleSwapTradeReviewDialog',
        token_from: tokenFrom,
        token_to: tokenTo,
        tx,
      })
      createErrorToast(e.message, false)
    },
    [trade?.amountIn?.currency, trade?.amountOut?.currency, trade?.tx],
  )

  const {
    mutateAsync: sendTransactionAsync,
    isPending: isWritePending,
    data: txHash,
  } = useSendTransaction({
    mutation: {
      onMutate: () => {
        // Set reference of current trade
        if (tradeRef && trade) {
          tradeRef.current = trade
        }
      },
      onSuccess: onSwapSuccess,
      onError: onSwapError,
    },
  })

  const write = useMemo(() => {
    if (!trade?.tx || !trade?.rawIntent || address !== trade.tx.from)
      return undefined

    const { to, data, value } = trade.tx
    const rawIntent = trade.rawIntent

    return async (confirm: () => void) => {
      try {
        setIntentFulfilled('pending')
        setSwapInProgress(true)

        // Lock the quoted rate before depositing
        const committed = await commitIntent(rawIntent)
        committedIntentIdRef.current = committed.intentId

        // Send deposit transaction
        await sendTransactionAsync({
          to,
          data,
          value,
        })
        confirm()
      } catch {
        setIntentFulfilled('idle')
        setSwapInProgress(false)
      }
    }
  }, [
    address,
    trade?.tx,
    trade?.rawIntent,
    sendTransactionAsync,
    setSwapInProgress,
  ])

  const { status: txStatus } = useWaitForTransactionReceipt({
    chainId: chainId,
    hash: txHash,
  })

  // Hold status at 'pending' until both the tx is mined AND the intent is fulfilled
  const status: 'pending' | 'success' | 'error' =
    intentFulfilled === 'error' || txStatus === 'error'
      ? 'error'
      : intentFulfilled === 'success' && txStatus === 'success'
        ? 'success'
        : 'pending'

  return {
    trade,
    tradeRef: tradeRef,
    write,
    isWritePending,
    txHash,
    status,
    slippagePercent,
    isSwapQueryFetching,
    isSwapQuerySuccess,
    isSwapQueryError,
    swapQueryError,
    isWrap,
    isUnwrap,
  }
}
